/**
 * TemplateFiller.js
 *
 * Template filling UI: fetches a template's variable schema,
 * renders DynamicForm for inputs, submits to /generate endpoint,
 * and transitions to PostGenActions on success.
 *
 * Props:
 *  - templateId: string — ID of the template to fill
 *  - onClose:    () => void — return to template list
 */

import React, { useState, useEffect, useCallback } from 'react';
import DynamicForm from './DynamicForm';
import PostGenActions from './PostGenActions';
import { apiCall } from '../../utils/apiUtils';
import { toast } from 'react-toastify';

const API_BASE = '/api/external/v1/doqsolv';

const TemplateFiller = ({ templateId, onClose }) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState({});
  const [generating, setGenerating] = useState(false);

  // Post-generation result
  const [genResult, setGenResult] = useState(null); // { document_id, template_name, pdf_file_id }

  // ── Fetch template ────────────────────────────────────────────────────

  useEffect(() => {
    const fetchTemplate = async () => {
      setLoading(true);
      try {
        const data = await apiCall(`${API_BASE}/templates/${templateId}`);
        setTemplate(data.template || data);
      } catch (err) {
        toast.error(err.message || 'Failed to load template');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchTemplate();
  }, [templateId, onClose]);

  // ── Handle form value changes ─────────────────────────────────────────

  const handleChange = useCallback((key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Generate document ─────────────────────────────────────────────────

  const handleGenerate = async () => {
    // Quick validation: check all required variables (standalone mode)
    const requiredVars = (template.variables || []).filter(
      (v) => v.is_required
    );
    const missing = requiredVars.filter((v) => !values[v.key]?.toString().trim());
    if (missing.length > 0) {
      toast.warning(`Please fill required fields: ${missing.map((v) => v.label).join(', ')}`);
      return;
    }

    setGenerating(true);
    try {
      const res = await apiCall(`${API_BASE}/generate`, {
        method: 'POST',
        data: {
          template_id: templateId,
          variables: values,
        },
      });

      setGenResult({
        document_id: res.document_id,
        template_name: res.template_name,
        pdf_file_id: res.pdf_file_id,
      });
    } catch (err) {
      toast.error(err.message || 'Document generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // ── Loading state ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20 text-gray-400">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mb-3" />
        <p className="text-sm">Loading template…</p>
      </div>
    );
  }

  // ── Post-generation view ──────────────────────────────────────────────

  if (genResult) {
    return (
      <PostGenActions
        documentId={genResult.document_id}
        templateName={genResult.template_name}
        pdfFileId={genResult.pdf_file_id}
        onBack={onClose}
      />
    );
  }

  // ── Fill Form ─────────────────────────────────────────────────────────

  const variables = template?.variables || [];
  const templateName = template?.name || 'Template';

  return (
    <div className="fade-in flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm px-6 py-4 mb-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition p-1"
            title="Back to Templates"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Fill Template</h2>
            <p className="text-xs text-gray-500">{templateName}</p>
          </div>
        </div>

        <button
          onClick={handleGenerate}
          disabled={generating}
          className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-lg shadow-sm transition"
          style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}
        >
          {generating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Generating…
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Generate Document
            </>
          )}
        </button>
      </div>

      {/* Form Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-6 max-w-3xl mx-auto">
          {/* Template Info */}
          {template?.description && (
            <div className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-100">
              <p className="text-sm text-indigo-700">
                <span className="font-medium">About this template:</span> {template.description}
              </p>
            </div>
          )}

          {/* Category badge */}
          {template?.category && (
            <div className="mb-4">
              <span className="text-xs font-medium px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full">
                {template.category}
              </span>
            </div>
          )}

          {/* Dynamic Form — standalone filling: show all variables */}
          <DynamicForm
            variables={variables}
            values={values}
            onChange={handleChange}
            showAllVariables={true}
          />

          {/* Standalone filling note */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
            <p className="text-xs text-gray-500 leading-relaxed">
              <span className="font-medium text-gray-600">ℹ️ Note:</span> All template variables
              are shown here for manual entry. When used through a stamp paper workflow,
              standard variables (Party Names, Stamp Details, etc.) are auto-populated from order data.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateFiller;
